<template>
  <div class="kachogawa-pickup-overview">
    <PageTitle :title="$t('kachogawaPickupOverview.title')" hideBtn>
      <template #btn1>
        <span v-if="queriedAt" class="text-sub text-gray-60">
          {{ $t('kachogawaPickupOverview.lastUpdated.text', { time: queriedAtText }) }}
        </span>
      </template>
    </PageTitle>

    <div class="ov-filter">
      <div class="ov-field">
        <label class="ov-field-label">{{ $t('kachogawaPickupOverview.filter.date.title') }}</label>
        <div class="ov-date-control">
          <BaseElSelect
            v-model="search.dateMode"
            class="ov-date-mode"
            @change="onDateModeChange"
          >
            <BaseElSelectOption
              v-for="mode in dateModeOptions"
              :key="mode.value"
              :value="mode.value"
              :label="$t(mode.label)"
            />
          </BaseElSelect>
          <!--
            🔴 key 不能拿掉：兩個 el-date-picker 只差 type，沒有 key 時 Vue 會判定
            可複用而沿用同一個實例。element-ui 換 panel 時不會清掉 vue-popper 快取的
            referenceElm，日曆會拿已經被移除的舊輸入框算位置 ⇒ 面板飛到畫面左上角。
          -->
          <el-date-picker
            v-if="search.dateMode === 'range'"
            key="date-picker-range"
            v-model="search.dateRange"
            class="ov-date-picker"
            type="daterange"
            :range-separator="$t('common.input.datepicker.rangeSeparator')"
            value-format="yyyy-MM-dd"
            :start-placeholder="$t('kachogawaPickupOverview.filter.startDate.placeholder')"
            :end-placeholder="$t('kachogawaPickupOverview.filter.endDate.placeholder')"
            :clearable="false"
            @change="refresh"
          />
          <el-date-picker
            v-else-if="search.dateMode === 'day'"
            key="date-picker-day"
            v-model="search.singleDate"
            class="ov-date-picker"
            type="date"
            value-format="yyyy-MM-dd"
            :placeholder="$t('kachogawaPickupOverview.filter.startDate.placeholder')"
            :clearable="false"
            @change="refresh"
          />
          <span v-else class="ov-date-today">{{ todayText }}</span>
        </div>
      </div>
      <div class="ov-field">
        <label class="ov-field-label">{{ $t('kachogawaPickupOverview.filter.category.title') }}</label>
        <BaseElSelect
          v-model="search.appointmentCategoryId"
          class="ov-category-select"
          @change="refresh"
        >
          <BaseElSelectOption
            :value="null"
            :label="$t('kachogawaPickupOverview.filter.category.all')"
          />
          <BaseElSelectOption
            v-for="category in categoryOptions"
            :key="category.id"
            :value="category.id"
            :label="category.name"
          />
        </BaseElSelect>
      </div>
      <div class="ov-field">
        <label class="ov-field-label">{{ $t('kachogawaPickupOverview.filter.status.title') }}</label>
        <div class="status-chips">
          <button
            v-for="option in statusOptions"
            :key="option.value"
            type="button"
            class="status-chip"
            :class="{ active: search.status === option.value }"
            @click="onStatusChange(option.value)"
          >
            {{ $t(option.name) }}
          </button>
        </div>
      </div>
    </div>

    <div v-loading="loading.overview" class="overview-content">
      <template v-if="overview">
        <PickupSummaryBar :summary="overview.summary" />

        <div class="overview-grid">
          <PickupHourlyDistribution
            :buckets="overview.hourlyBuckets"
            @view-detail="onViewDetail"
          />
          <PickupPivotTable
            :services="overview.summary.byService"
            :rows="overview.rows"
            :totalPrepareCount="overview.summary.totalPrepareCount"
          />
        </div>
      </template>
      <EmptyBlock v-else-if="!loading.overview" />
    </div>

    <PickupReservationDialog
      v-if="viewDetail.visible"
      :bucket="viewDetail.bucket"
      :status="search.status"
      :appointmentCategoryId="search.appointmentCategoryId"
      :categoryName="selectedCategoryName"
      :categoryOptions="categoryOptions"
      @close="closeViewDetail"
      @updated="refresh"
    />
  </div>
</template>

<script>
import { computed, defineComponent, onMounted, reactive, ref } from 'vue'
import dayjs from '@/lib/dayjs'
import { formatDate } from '@/utils/date'
import { get } from 'lodash'
import PageTitle from '@/components/Title/PageTitle.vue'
import EmptyBlock from '@/components/EmptyBlock.vue'
import { i18n } from '@/plugins/i18n/i18n'
import { useTable } from '@/use/table'
import { orderStatusConfig } from '@/config/reservation'
import { GetKachogawaAppointmentPickupOverview } from '@/api/kachogawaAppointmentPickup'
import { GetServiceCategory } from '@/api/serviceCategory'
import PickupSummaryBar from './components/PickupSummaryBar.vue'
import PickupHourlyDistribution from './components/PickupHourlyDistribution.vue'
import PickupPivotTable from './components/PickupPivotTable.vue'
import PickupReservationDialog from './components/PickupReservationDialog.vue'

export default defineComponent({
  name: 'KachogawaPickupOverview',
  components: {
    PageTitle,
    EmptyBlock,
    PickupSummaryBar,
    PickupHourlyDistribution,
    PickupPivotTable,
    PickupReservationDialog,
  },
  setup () {
    const { shopId, loading, extendData } = useTable()

    const overview = ref(null)
    const queriedAt = ref(null)
    const categoryOptions = ref([])

    const today = dayjs().format('YYYY-MM-DD')
    const search = reactive({
      dateMode: 'today', // today | day | range
      singleDate: today,
      dateRange: [today, today],
      appointmentCategoryId: null,
      status: 'wait',
    })

    const viewDetail = reactive({
      visible: false,
      bucket: null,
    })

    const dateModeOptions = [
      { value: 'today', label: 'kachogawaPickupOverview.filter.dateMode.today' },
      { value: 'day', label: 'kachogawaPickupOverview.filter.dateMode.day' },
      { value: 'range', label: 'kachogawaPickupOverview.filter.dateMode.range' },
    ]

    const todayText = computed(() => today)

    const dateRangeForQuery = computed(() => {
      if (search.dateMode === 'today') return [today, today]
      if (search.dateMode === 'day') return [search.singleDate, search.singleDate]
      return search.dateRange
    })

    // 沿用既有 orderStatusConfig（wait/confirm/complete/absence/cancel）以共用 i18n 文案
    const statusOptions = computed(() => [
      orderStatusConfig.wait,
      orderStatusConfig.confirm,
      orderStatusConfig.complete,
      orderStatusConfig.absence,
      orderStatusConfig.cancel,
    ])

    const queriedAtText = computed(() => {
      return queriedAt.value ? formatDate(queriedAt.value) : ''
    })

    // 明細 Modal 標題用：未選 = 全部門市；已選 = 對應分店名稱
    const selectedCategoryName = computed(() => {
      if (!search.appointmentCategoryId) return i18n.t('kachogawaPickupOverview.filter.category.all')
      const found = categoryOptions.value.find((c) => c.id === search.appointmentCategoryId)
      return get(found, 'name', '')
    })

    const getCategoryOptions = async () => {
      try {
        const res = await GetServiceCategory({ shopId: shopId.value, start: 0, limit: 200 })
        categoryOptions.value = res || []
      } catch (err) {
        categoryOptions.value = []
        window.$message.error(err)
      }
    }

    const getOverview = async () => {
      extendData('loading', { overview: true })
      try {
        const [startDate, endDate] = dateRangeForQuery.value
        const [res, err] = await GetKachogawaAppointmentPickupOverview({
          shopId: shopId.value,
          startDate,
          endDate,
          appointmentCategoryId: search.appointmentCategoryId || undefined,
          status: search.status,
        })
        if (err) {
          overview.value = null
          window.$message.error(err)
          return
        }
        overview.value = res
        queriedAt.value = get(res, 'queriedAt', null)
      } catch (err) {
        overview.value = null
        window.$message.error(err)
      } finally {
        extendData('loading', { overview: false })
      }
    }

    const refresh = async () => {
      await getOverview()
    }

    const onStatusChange = (value) => {
      if (search.status === value) return
      search.status = value
      refresh()
    }

    const onDateModeChange = () => {
      // 「今天」可直接查；day/range 等使用者選完日期由 picker @change 觸發
      if (search.dateMode === 'today') refresh()
    }

    const onViewDetail = (bucket) => {
      viewDetail.bucket = bucket
      viewDetail.visible = true
    }

    const closeViewDetail = () => {
      viewDetail.visible = false
      viewDetail.bucket = null
    }

    onMounted(async () => {
      // 兩個請求互相獨立，並行加快首屏
      await Promise.all([getCategoryOptions(), getOverview()])
    })

    return {
      loading,
      overview,
      queriedAt,
      queriedAtText,
      categoryOptions,
      search,
      dateModeOptions,
      todayText,
      statusOptions,
      selectedCategoryName,
      viewDetail,
      refresh,
      onStatusChange,
      onDateModeChange,
      onViewDetail,
      closeViewDetail,
    }
  },
})
</script>

<style lang="postcss" scoped>
.kachogawa-pickup-overview {
  @apply p-[24px];
}

.ov-filter {
  @apply flex flex-wrap items-end gap-[20px] mb-[24px];
  @apply bg-white border border-gray-40 rounded-md py-[20px] px-[24px];
}

.ov-field {
  @apply flex flex-col gap-[6px];
}

.ov-field-label {
  @apply text-[12px] text-gray-60 font-medium;
}

.ov-date-control {
  @apply flex items-center gap-[8px];
}

.ov-date-mode {
  @apply w-[110px] flex-shrink-0;
}

.ov-date-picker {
  @apply w-[320px];
}

.ov-date-today {
  @apply flex items-center h-[36px] px-[12px] text-normal text-gray-100;
  @apply border border-gray-40 rounded-sub bg-gray-20 min-w-[140px];
}

.ov-category-select {
  @apply w-[224px];
}

.overview-content {
  @apply min-h-[200px];
}

.overview-grid {
  @apply grid gap-[20px] mt-[24px];
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}

@media (max-width: 1200px) {
  .overview-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.status-chips {
  @apply inline-flex h-[36px] border border-gray-40 rounded-normal overflow-hidden;
}

.status-chip {
  @apply flex items-center px-[14px] text-sub bg-white text-gray-80 cursor-pointer whitespace-nowrap;
  @apply border-0 border-r border-gray-40;
}

.status-chip:last-child {
  @apply border-r-0;
}

.status-chip.active {
  @apply bg-primary-100 text-white font-medium;
}

/* 鎖死內部 el-select / picker 寬度，避免被 flex 撐滿蓋到其他欄位 */
:deep(.ov-date-mode .el-select),
:deep(.ov-date-mode .el-input) {
  @apply w-[110px];
}

:deep(.ov-category-select.el-select),
:deep(.ov-category-select .el-input) {
  @apply w-[224px];
}

:deep(.ov-date-picker.el-date-editor) {
  @apply w-[320px];
}

/* el-date-picker daterange 預設 separator 太窄會把「至」字切掉 */
:deep(.ov-date-picker .el-range-separator) {
  @apply w-[24px] px-0 flex-shrink-0;
}
</style>
